import React from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  TrendingUp,
  Shield,
  Calculator,
  Users,
  Rocket,
  Mail,
  CheckCircle,
} from "lucide-react";
import SEO from "../components/SEO";
import { getBreadcrumbSchema } from "../utils/schema";
import "./Services.css";

export const Services = () => {
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Services', url: '/services' }
  ]);

  const services = [
    {
      id: "annual-accounts",
      icon: <FileText size={40} />,
      title: "Annual Accounts",
      subtitle: "Year-End Financial Reporting & Filing",
      description:
        "Professional preparation of statutory accounts for limited companies and sole traders across Wolverhampton and the West Midlands. Our comprehensive annual accounts service ensures full compliance with Companies House and HMRC requirements.",
      features: [
        "Company year-end accounts preparation",
        "Sole trader and partnership accounts",
        "Companies House filing",
        "Directors' reports and financial statements",
        "Management accounts and analysis",
        "Compliance with FRS 102 and FRS 105",
      ],
      benefits:
        "Our meticulous approach ensures your accounts are accurate, compliant, and filed on time, avoiding penalties while providing valuable insights into your business performance. Ideal for limited companies in Wolverhampton, Dudley, and Walsall seeking professional accounting support.",
    },
    {
      id: "tax-planning",
      icon: <TrendingUp size={40} />,
      title: "Tax Planning & Advisory",
      subtitle: "Strategic Tax Efficiency Solutions",
      description:
        "Expert tax planning services designed to minimize your tax liabilities while ensuring full HMRC compliance. Our FCCA-qualified accountant provides strategic advice tailored to your business structure and personal circumstances.",
      features: [
        "Corporation tax planning strategies",
        "Personal tax efficiency advice",
        "Capital gains tax mitigation",
        "Inheritance tax planning",
        "Dividend vs salary optimization",
      ],
      benefits:
        "With 14+ years of UK tax experience, we identify legitimate opportunities to reduce your tax burden through proper planning and structure optimization. Serving business owners, landlords, and high earners across the West Midlands with proven tax-saving strategies.",
    },
    {
      id: "hmrc-support",
      icon: <Shield size={40} />,
      title: "HMRC Specialist Support",
      subtitle: "Expert Representation & Compliance",
      description:
        "Professional support for HMRC enquiries, investigations, and compliance matters. Our specialist knowledge of UK tax law and HMRC procedures provides you with confident representation when it matters most.",
      features: [
        "HMRC enquiry and investigation support",
        "Tax dispute resolution",
        "Compliance checks assistance",
        "VAT inspection representation",
        "Penalty appeals and negotiations",
        "Full correspondence management",
      ],
      benefits:
        "Navigate HMRC investigations with expert guidance and professional representation. We handle all communications, compile required documentation, and protect your interests throughout the process. Essential support for Wolverhampton businesses facing HMRC scrutiny.",
    },
    {
      id: "self-assessment",
      icon: <Calculator size={40} />,
      title: "Self Assessment Tax Returns",
      subtitle: "Personal Tax Return Preparation",
      description:
        "Accurate and timely Self Assessment tax return preparation for individuals, freelancers, landlords, and partnerships. We ensure you claim all eligible reliefs and allowances while meeting HMRC deadlines.",
      features: [
        "Personal tax return (SA100) completion",
        "Landlord property income returns",
        "Partnership tax returns (SA800)",
        "Capital gains tax reporting",
        "Foreign income declarations",
        "Tax refund claims",
      ],
      benefits:
        "Avoid penalties and maximize your tax position with our expert Self Assessment service. Ideal for self-employed professionals, contractors, landlords, and anyone with complex tax affairs in Wolverhampton and surrounding areas.",
    },
    {
      id: "payroll-vat",
      icon: <Users size={40} />,
      title: "Payroll & VAT Services",
      subtitle: "Complete Compliance Management",
      description:
        "Comprehensive payroll management and VAT return services ensuring full compliance with HMRC regulations. We handle all aspects of your payroll and VAT obligations with accuracy and efficiency.",
      features: [
        "Monthly payroll processing (RTI)",
        "P60 and P45 documentation",
        "VAT return preparation and filing",
        "Making Tax Digital (MTD) compliance",
      ],
      benefits:
        "Focus on your business while we manage your payroll and VAT compliance. Our service includes all HMRC submissions, employee documentation, and VAT returns completed accurately and on time. Supporting employers and VAT-registered businesses across the West Midlands.",
    },
    {
      id: "start-up",
      icon: <Rocket size={40} />,
      title: "Business Start-Up Support",
      subtitle: "Launch Your Business Right",
      description:
        "Comprehensive start-up support for new businesses in Wolverhampton. From company formation to accounting system setup, we provide the foundation for long-term financial success.",
      features: [
        "Company formation and registration",
        "Business structure advice (sole trader vs limited)",
        "HMRC registrations (PAYE, VAT, CT)",
        "Business plan financial sections",
        "First-year accounting support",
      ],
      benefits:
        "Start your business journey with expert guidance and proper financial foundations. We help Wolverhampton entrepreneurs choose the right structure, complete all registrations, and establish efficient accounting systems. Essential support for anyone launching a new venture in the West Midlands.",
    },
  ];

  return (
    <div className="services-page">
      <SEO
        title="Accounting Services Wolverhampton | Annual Accounts, Tax Planning & HMRC Support"
        description="Comprehensive accounting services in Wolverhampton: Annual accounts, tax planning, HMRC support, self assessment, payroll & VAT, business start-up. FCCA qualified accountant with 15+ years experience."
        canonical="/services"
        keywords="accounting services Wolverhampton, annual accounts Wolverhampton, tax planning Wolverhampton, HMRC support, self assessment Wolverhampton, payroll services West Midlands"
        schema={breadcrumbSchema}
      />
      {/* Hero Section */}
      <section className="services-hero">
        <div className="container">
          <div className="services-hero-content">
            <h1>Professional Accounting Services in Wolverhampton</h1>
            <p>
              Comprehensive financial solutions for businesses and individuals
              across the West Midlands. Expert service backed by MAAT
              qualifications with 15+ years of UK practice experience.
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="section services-detail">
        <div className="container">
          {services.map((service, index) => (
            <div
              key={service.id}
              id={service.id}
              className={`service-detail-card ${
                index % 2 === 0 ? "card-left" : "card-right"
              }`}
            >
              <div className="service-detail-icon">{service.icon}</div>
              <div className="service-detail-content">
                <h2>{service.title}</h2>
                <h3 className="service-subtitle">{service.subtitle}</h3>
                <p className="service-description">{service.description}</p>

                <div className="service-features">
                  <h4>What's Included:</h4>
                  <div className="features-grid">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="feature-item">
                        <CheckCircle size={18} />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="service-benefits">
                  <h4>Benefits:</h4>
                  <p>{service.benefits}</p>
                </div>

                <Link
                  to="/contact"
                  className="btn-primary"
                  data-testid={`services-cta-${service.id}`}
                >
                  <Mail size={20} />
                  Enquire About This Service
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="section section-alt service-areas">
        <div className="container">
          <div className="text-center">
            <h2>Serving Wolverhampton and the West Midlands</h2>
            <p className="section-subtitle">
              We provide professional accounting services to clients across
              Wolverhampton, Dudley, Walsall, Bilston, Willenhall, and
              throughout the West Midlands region.
            </p>
            <div className="areas-list">
              <span className="area-tag">Wolverhampton</span>
              <span className="area-tag">Birmingham</span>
              <span className="area-tag">Coventry</span>
              <span className="area-tag">Leicester</span>
              <span className="area-tag">Leamington Spa</span>
              <span className="area-tag">Warwickshire</span>
              <span className="area-tag">Rugby</span>
              <span className="area-tag">Dudley</span>
              <span className="area-tag">Walsall</span>
              <span className="area-tag">Bilston</span>
              <span className="area-tag">Willenhall</span>
              <span className="area-tag">West Bromwich</span>
              <span className="area-tag">Sedgley</span>
              <span className="area-tag">Wednesfield</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section services-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Get Started?</h2>
            <p>
              Contact our FCCA-qualified accountant today for a consultation
              tailored to your specific needs.
            </p>
            <Link
              to="/contact"
              className="btn-primary btn-large"
              data-testid="services-cta-bottom"
            >
              <Mail size={22} />
              Get Expert Advice
            </Link>
            <div className="cta-links">
              <Link to="/pricing" className="cta-link">
                View Pricing
              </Link>
              <Link to="/about" className="cta-link">
                About Our Practice
              </Link>
              <Link to="/faq" className="cta-link">
                Common Questions
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
